import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useNavigate, useLocation } from "react-router-dom";
import confetti from "canvas-confetti";
import api from "./api";
import { useAuth } from "./auth";
import { useAccounts } from "./accounts";

// Fires a small celebration when the walkthrough completes. Three staggered
// bursts from bottom corners + a top-center pop, using the app's palette so
// the confetti feels on-brand instead of generic. No dependency on any DOM
// node — works even during the final navigation back to the dashboard.
function celebrateTourComplete() {
  const base = { spread: 80, startVelocity: 48, gravity: 0.9, ticks: 220, zIndex: 10002 };
  // Left burst — mint + gold (matches "positive" chips)
  confetti({
    ...base,
    particleCount: 110,
    origin: { x: 0.15, y: 0.75 },
    colors: ["#b5e4ca", "#12854a", "#fdecc8", "#c48b00"],
  });
  // Right burst — lavender + pink (matches Snap Receipt + accents)
  setTimeout(() => {
    confetti({
      ...base,
      particleCount: 110,
      origin: { x: 0.85, y: 0.75 },
      colors: ["#d9d4f1", "#7b6ad0", "#ffb3b3", "#f4b4d9"],
    });
  }, 220);
  // Center-top pop — smaller, quicker, brand black + mint
  setTimeout(() => {
    confetti({
      ...base,
      particleCount: 70,
      spread: 110,
      startVelocity: 35,
      origin: { x: 0.5, y: 0.35 },
      colors: ["#1a1a1a", "#b5e4ca", "#fdecc8", "#d9d4f1"],
    });
  }, 440);
}

// Ordered steps for the guided walkthrough. Each step:
//   path         — route to navigate to before showing the step
//   testid       — data-testid of the element to spotlight (omit for centered card)
//   dock         — 'bottom' pins the tooltip to the bottom of the viewport so
//                  it never covers form inputs the user needs to reach.
//   title/body   — copy shown in the tooltip
//   waitFor      — 'account' | 'txn' | null: hides "Next" until the user does the thing
//   menuLabel    — short label shown in the step-picker menu (falls back to title)
//   videoUrl     — OPTIONAL. Loom or YouTube URL for a per-step demo video.
//                  Once you've recorded a Loom clip, paste the URL here and
//                  users will see a "▶ Watch demo" button on that step's card.
//                  Accepted formats:
//                    • https://www.loom.com/share/<id>            (auto-embedded)
//                    • https://www.loom.com/embed/<id>            (used as-is)
//                    • https://youtu.be/<id>                      (auto-embedded)
//                    • https://www.youtube.com/watch?v=<id>       (auto-embedded)
//                    • https://www.youtube.com/embed/<id>         (used as-is)
//                    • Any direct .mp4 URL — rendered in a native <video>
const STEPS = [
  {
    id: "welcome",
    path: "/",
    menuLabel: "Welcome",
    title: "Yo — welcome to SyncBook 👋",
    body:
      "Let's get you rolling in like 30 seconds. Two tiny steps and you're done — everything else you'll discover as you go.",
    primary: "Let's go",
    centered: true,
    videoUrl: null,
  },
  {
    id: "add-account",
    path: "/accounts",
    testid: "add-account-form",
    dock: "bottom",
    menuLabel: "Add your money",
    title: "How much cash you got?",
    body:
      "Name your account (like \"Chase\" or \"Piggy\") and drop your current balance. This is your starting line.",
    primary: "Done",
    waitFor: "account",
    videoUrl: null,
  },
  {
    id: "add-txn",
    path: "/add",
    testid: "txn-amount",
    dock: "bottom",
    menuLabel: "Log a spend",
    title: "Log something you spent today",
    body:
      "Try that iced latte: amount $6.50, who \"Starbucks\". SyncBook remembers so next time you'll type even less.",
    primary: "Saved",
    waitFor: "txn",
    videoUrl: null,
  },
  {
    id: "done",
    path: "/",
    menuLabel: "Finish 🎉",
    title: "You're set 🎉",
    body:
      "That's it! Snap receipts with the camera button any time. Poke around \"More\" when you're ready — budgets, forecasts, SMS, all in there.",
    primary: "Finish",
    centered: true,
    videoUrl: null,
  },
];

const TourCtx = createContext(null);

export function TourProvider({ children }) {
  const { user, refreshUser } = useAuth();
  const { accounts, reload: reloadAccounts } = useAccounts();
  const nav = useNavigate();
  const loc = useLocation();

  // Per-step video URL overrides fetched from the backend. Merged onto STEPS
  // so admins can point steps at Loom / YouTube clips WITHOUT redeploying.
  const [videoOverrides, setVideoOverrides] = useState({});
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/tour-videos");
        if (!cancelled) setVideoOverrides(data || {});
      } catch {
        /* endpoint might be missing on very old backends — ignore */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user?.id]);

  // Merged step list with runtime video overrides applied
  const stepsWithVideos = useMemo(
    () => STEPS.map((s) => ({ ...s, videoUrl: videoOverrides[s.id] || s.videoUrl || null })),
    [videoOverrides]
  );

  // -1 means the tour is not running. Persist to `localStorage` (not session)
  // so closing the browser and coming back later picks up right where they
  // left off — key requirement for a multi-step walkthrough that people rarely
  // finish in one sitting.
  const [stepIdx, setStepIdx] = useState(() => {
    const raw = localStorage.getItem("cb_tour_step");
    const n = raw ? parseInt(raw, 10) : -1;
    return Number.isFinite(n) ? n : -1;
  });

  // Collapsed = tour card shrunk to a tiny pill so it doesn't cover form
  // inputs. Also persisted so scrolling / refreshing doesn't re-expand it.
  const [collapsed, setCollapsed] = useState(
    () => localStorage.getItem("cb_tour_collapsed") === "1"
  );

  // Persist step so hard refreshes mid-tour don't dump the user
  useEffect(() => {
    if (stepIdx < 0) localStorage.removeItem("cb_tour_step");
    else localStorage.setItem("cb_tour_step", String(stepIdx));
  }, [stepIdx]);

  useEffect(() => {
    if (collapsed) localStorage.setItem("cb_tour_collapsed", "1");
    else localStorage.removeItem("cb_tour_collapsed");
  }, [collapsed]);

  // Auto-open once for brand-new users. Trigger conditions:
  //  - user is logged in
  //  - onboarding_completed_at is null/missing
  //  - not already running
  //  - not impersonating (admin viewing another user shouldn't get the wizard)
  const autoStartedRef = React.useRef(
    // Consider the tour "already auto-started" if there's a live step in
    // localStorage — otherwise the auto-start effect would re-open the
    // wizard immediately after the user finishes it.
    sessionStorage.getItem("cb_tour_step") !== null ||
      localStorage.getItem("cb_tour_step") !== null
  );
  useEffect(() => {
    if (!user) return;
    if (autoStartedRef.current) return;
    if (user.impersonation) return;
    if (user.onboarding_completed_at) return;
    if (stepIdx >= 0) return;
    autoStartedRef.current = true;
    // Kick off on the next microtask so the Dashboard commits first render;
    // NOT setTimeout, because effect cleanup would cancel it if `user`
    // re-refreshes (e.g. via refreshUser) before the timer fires.
    Promise.resolve().then(() => setStepIdx(0));
  }, [user, stepIdx]);

  // Track "waitFor" gates so the primary button only unlocks when the user has
  // actually done the thing (added an account, saved a txn, etc.). We snapshot
  // the counts when a gated step starts and unlock as soon as they grow.
  const [gateSnapshot, setGateSnapshot] = useState({ accounts: 0, txns: 0 });
  const [txnCount, setTxnCount] = useState(0);

  const refetchTxnCount = useCallback(async () => {
    try {
      const { data } = await api.get("/transactions");
      setTxnCount((data || []).length);
    } catch {
      /* ignore */
    }
  }, []);

  const currentStep =
    stepIdx >= 0 && stepIdx < stepsWithVideos.length ? stepsWithVideos[stepIdx] : null;

  // When a gated step becomes active, snapshot the current counts. The primary
  // button is enabled only when the live count exceeds the snapshot.
  useEffect(() => {
    if (!currentStep) return;
    if (currentStep.waitFor === "account") {
      setGateSnapshot((s) => ({ ...s, accounts: accounts.length }));
    }
    if (currentStep.waitFor === "txn") {
      refetchTxnCount().then(() => {
        setGateSnapshot((s) => ({ ...s, txns: 0 })); // reset then compare after fetch
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stepIdx]);

  // Poll transactions while the txn-gated step is showing so the button unlocks
  // as soon as the user saves. Cheap because we only poll during that one step.
  useEffect(() => {
    if (!currentStep || currentStep.waitFor !== "txn") return;
    const int = setInterval(refetchTxnCount, 2500);
    return () => clearInterval(int);
  }, [currentStep, refetchTxnCount]);

  // Similarly poll accounts.length via the AccountsProvider reload
  useEffect(() => {
    if (!currentStep || currentStep.waitFor !== "account") return;
    const int = setInterval(() => reloadAccounts(), 2000);
    return () => clearInterval(int);
  }, [currentStep, reloadAccounts]);

  // We DON'T auto-navigate on every stepIdx change via a global effect —
  // that turned out to kidnap admins visiting /tour-videos or /profile back
  // to "/" whenever the tour auto-resumed. Instead each explicit action
  // (advance, skipStep, goToStep, start) is responsible for calling nav()
  // to the target step's path. This means:
  //   - Auto-open on login (setStepIdx via useEffect) does NOT navigate;
  //     the welcome card just appears centered wherever the user is.
  //   - Clicking Next, Skip, or a menu row DOES navigate to that step's path.

  // Celebrate the moment the user reaches the final "done" card so the
  // "You're all set 🎉" tooltip lands with a burst behind it. Fires exactly
  // once per session (autoStartedRef doubles as a nice guard for restarts
  // because start() resets it not — see below).
  const celebratedRef = React.useRef(false);
  useEffect(() => {
    if (currentStep?.id === "done" && !celebratedRef.current) {
      celebratedRef.current = true;
      celebrateTourComplete();
    }
    if (!currentStep) {
      // Tour closed — allow another celebration if the user restarts later
      celebratedRef.current = false;
    }
  }, [currentStep]);

  const advance = useCallback(async () => {
    let next = stepIdx + 1;
    // Skip Pro-only steps for already-paid users so we don't push an upsell.
    while (next < STEPS.length && STEPS[next].skipIfPaid && user?.plan === "paid" && !user?.trial_active) {
      next += 1;
    }
    if (next >= STEPS.length) {
      // End of tour — mark complete
      setStepIdx(-1);
      setCollapsed(false); // reset UI state so next restart is clean
      try {
        await api.post("/auth/onboarding/complete");
        await refreshUser();
      } catch {
        /* non-blocking */
      }
      return;
    }
    setStepIdx(next);
    // Explicit advance → route to the next step's screen
    const target = STEPS[next].path;
    if (target && loc.pathname !== target) nav(target);
  }, [stepIdx, user, refreshUser, nav, loc.pathname]);

  const skipStep = useCallback(() => {
    // "Skip this step" — jump forward without waiting on a gate. Same as
    // advance() but bypasses the paid-user skipIfPaid loop guard because the
    // user explicitly asked to move on. If we're on the last step, treat it
    // like a full finish.
    if (stepIdx >= STEPS.length - 1) {
      advance();
      return;
    }
    const next = stepIdx + 1;
    setStepIdx(next);
    const target = STEPS[next].path;
    if (target && loc.pathname !== target) nav(target);
  }, [stepIdx, advance, nav, loc.pathname]);

  const goToStep = useCallback(
    (idx) => {
      // Direct jump — used by the Profile "Restart walkthrough" button and
      // future per-step nav.
      if (idx < 0 || idx >= STEPS.length) return;
      setStepIdx(idx);
      const target = STEPS[idx].path;
      if (target && loc.pathname !== target) nav(target);
    },
    [nav, loc.pathname]
  );

  const skip = useCallback(async () => {
    // "Skip tour" text link — user wants to be done with it. Marks
    // onboarding complete so it won't auto-open again.
    setStepIdx(-1);
    try {
      await api.post("/auth/onboarding/complete");
      await refreshUser();
    } catch {
      /* non-blocking */
    }
  }, [refreshUser]);

  const close = useCallback(() => {
    // X button — temporary close. Does NOT mark onboarding complete, so if
    // the user hasn't been through the whole tour yet the wizard will still
    // auto-open next session. Users can also relaunch from Profile.
    setStepIdx(-1);
  }, []);

  const start = useCallback(async () => {
    // Reset the flag so a re-run doesn't get auto-closed
    try {
      await api.post("/auth/onboarding/reset");
      await refreshUser();
    } catch {
      /* non-blocking */
    }
    setCollapsed(false); // fresh tour = fully expanded card
    setStepIdx(0);
    // Send them to the welcome screen so the tour feels like a proper reboot
    if (loc.pathname !== STEPS[0].path) nav(STEPS[0].path);
  }, [refreshUser, nav, loc.pathname]);

  // Gate check for the primary button
  const gateOk = useMemo(() => {
    if (!currentStep?.waitFor) return true;
    if (currentStep.waitFor === "account") return accounts.length > gateSnapshot.accounts;
    if (currentStep.waitFor === "txn") return txnCount > 0;
    return true;
  }, [currentStep, accounts.length, gateSnapshot, txnCount]);

  // Auto-advance when a gate opens so the user doesn't have to hunt for the
  // tour card after saving. Short delay so the toast / DOM update lands first
  // and they visually confirm the action worked before moving on.
  const autoAdvancedForStepRef = React.useRef(null);
  useEffect(() => {
    if (!currentStep?.waitFor) return;
    if (!gateOk) return;
    if (autoAdvancedForStepRef.current === currentStep.id) return;
    autoAdvancedForStepRef.current = currentStep.id;
    const t = setTimeout(() => {
      // Guard: only advance if still on the same step (user might have hit
      // Skip / Close in the interim).
      setStepIdx((prev) => (prev === stepIdx ? prev + 1 : prev));
    }, 900);
    return () => clearTimeout(t);
  }, [gateOk, currentStep, stepIdx]);

  const skipToSnap = useCallback(async () => {
    // "Snap a receipt right now" shortcut on the welcome step — kids who want
    // to dive in hands-on skip typing entirely. We mark onboarding complete
    // AND jump straight to /snap; the auto-save in SnapReceipt handles the
    // rest, so the first successful scan effectively becomes their first
    // transaction.
    setStepIdx(-1);
    setCollapsed(false);
    try {
      await api.post("/auth/onboarding/complete");
      await refreshUser();
    } catch {
      /* non-blocking */
    }
    nav("/snap");
  }, [refreshUser, nav]);

  const value = useMemo(
    () => ({
      running: stepIdx >= 0,
      step: currentStep,
      stepIdx,
      totalSteps: STEPS.length,
      steps: stepsWithVideos,
      advance,
      skip,
      skipStep,
      skipToSnap,
      close,
      start,
      goToStep,
      gateOk,
      collapsed,
      setCollapsed,
    }),
    [stepIdx, currentStep, advance, skip, skipStep, skipToSnap, close, start, goToStep, gateOk, collapsed, stepsWithVideos]
  );

  return <TourCtx.Provider value={value}>{children}</TourCtx.Provider>;
}

export const useTour = () => useContext(TourCtx);
